/**
 * The two things this screen needs to know about the frame it is rendered in,
 * and neither of them is a breakpoint.
 *
 * `Shell` moved scrolling off the document and onto one element (`styles.css`
 * makes `html, body` `overflow: hidden`). That is right for a phone, and it
 * quietly invalidates every `window.scrollY` / `window.innerHeight` calculation
 * a screen was doing — including this one's "follow the playhead", which used
 * to compare a turn's box against `window.innerHeight` and would now be
 * comparing against a viewport that never moves.
 *
 * So: find the real scroller, and work out how much of its top edge is covered
 * by pinned chrome. Both are read from the DOM rather than passed down as
 * numbers, because both change with the breakpoint AND with the roster size —
 * the player is one lane taller per speaker — and a number computed in one
 * component is a number that is wrong in the other.
 */

import * as React from 'react'

/**
 * The element that actually scrolls, walking up from anywhere inside it.
 *
 * Deliberately NOT `document.querySelector('[data-scroll-restoration-id]')`,
 * even though `Shell` documents that attribute as stable: the call screens and
 * any future embed render this feature outside that shell, and a screen that
 * hard-codes its frame's selector breaks silently the first time it is reused.
 * Walking up finds whatever is really there, including the document scroller.
 */
export function findScroller(from: Element | null | undefined): HTMLElement | null {
  let node = from?.parentElement ?? null
  while (node) {
    const overflow = getComputedStyle(node).overflowY
    if (overflow === 'auto' || overflow === 'scroll') return node
    node = node.parentElement
  }
  return document.scrollingElement as HTMLElement | null
}

/**
 * Elements marked `data-era-spine` are the pinned chrome — the player, and on a
 * phone the tab strip under it.
 *
 * The attribute is the contract, not the class list: whether a given element is
 * actually pinned right now is a CSS decision (`max-sm:sticky lg:sticky`), and
 * the honest way to ask is `getComputedStyle().position`, not to re-derive the
 * breakpoint in JavaScript and hope the two stay in agreement.
 */
export const SPINE_ATTR = 'data-era-spine'

/**
 * How many pixels of the scroller's top edge are hidden behind pinned chrome.
 *
 * Anything scrolled into that band is invisible, so it is the offset every
 * "scroll this into view" on the screen has to respect. Returns 0 when nothing
 * is pinned, which is the tablet case and the case where the spine has scrolled
 * away above.
 */
export function spineHeadroom(scroller: HTMLElement | null): number {
  if (!scroller) return 0
  const top = scroller.getBoundingClientRect().top
  let deepest = 0
  for (const element of document.querySelectorAll<HTMLElement>(`[${SPINE_ATTR}]`)) {
    if (getComputedStyle(element).position !== 'sticky') continue
    deepest = Math.max(deepest, element.getBoundingClientRect().bottom - top)
  }
  return Math.max(0, deepest)
}

/**
 * True when the primary pointer is a finger.
 *
 * A WIDTH query is the wrong question for anything about touching, and gets it
 * wrong in a way that is easy to miss: an iPhone in landscape is 844 CSS px
 * wide and sails over every phone breakpoint, while a 500px-wide desktop window
 * is under all of them. `styles.css` already keys its 16px input floor on
 * `pointer: coarse` for exactly this reason; this is the same question asked
 * from JavaScript.
 */
export function useCoarsePointer(): boolean {
  return React.useSyncExternalStore(
    subscribeToCoarsePointer,
    () => window.matchMedia('(pointer: coarse)').matches,
    // No pointer at all during SSR/prerender. `false` keeps the desktop
    // behaviour, which is the one that is safe to be wrong about.
    () => false,
  )
}

function subscribeToCoarsePointer(onChange: () => void): () => void {
  const query = window.matchMedia('(pointer: coarse)')
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
